/**
 * InstallPrompt.jsx — PWA Install Banner
 *
 * Shows a subtle bottom sheet prompting install after:
 *  - 3+ visits (tracked in localStorage)
 *  - User hasn't dismissed in this session
 *  - App is not already installed (standalone mode)
 *
 * Uses the beforeinstallprompt event (Chrome/Edge/Samsung)
 * and falls back to iOS instructions.
 */

import { useState, useEffect, useCallback } from "react";

const VISIT_KEY = "hh_visit_count";
const DISMISSED_KEY = "hh_install_dismissed";
const MIN_VISITS = 3;

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return;

    // Don't show if dismissed this session
    if (sessionStorage.getItem(DISMISSED_KEY)) return;

    // Track visits
    const visits = parseInt(localStorage.getItem(VISIT_KEY) || "0") + 1;
    localStorage.setItem(VISIT_KEY, String(visits));

    // Not enough visits yet
    if (visits < MIN_VISITS) return;

    // iOS detection (no beforeinstallprompt)
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);

    if (iOS) {
      // Show iOS manual instructions after a delay
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Chrome/Edge/Samsung — listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  }, []);

  if (!showBanner) return null;

  return (
    <div className="hh-install-banner">
      <div className="hh-install-icon">🐬</div>
      <div className="hh-install-text">
        <div className="hh-install-title">Add HealHub to Home Screen</div>
        <div className="hh-install-desc">
          {isIOS
            ? "Tap the share button, then \"Add to Home Screen\""
            : "Quick access, offline mode, and a native app feel"
          }
        </div>
      </div>
      <div className="hh-install-actions">
        {!isIOS && deferredPrompt && (
          <button className="hh-install-btn" onClick={handleInstall}>
            Install
          </button>
        )}
        <button className="hh-install-dismiss" onClick={handleDismiss}>
          {isIOS ? "Got it" : "Later"}
        </button>
      </div>
    </div>
  );
}